import Button from '@rio-cloud/rio-uikit/Button';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { type ReactElement, type ReactNode, useEffect } from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { sendError } from '../../../configuration/lang/services';
import type { RootState } from '../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../api/redux/api.redux';
import { ApiCalls } from '../api/redux/types';
import { type BookingSuccessMessage, SUCCESSFULLY_BOOKED } from '../common/postMessage.types';
import { MARKETPLACE_CUSTOMER_CENTER_CONTRACTS } from '../common/routes';
import { sendIFrameCloseMessageToParent } from '../common/utils/iframeUtils';
import { getApplicationLink, getOpenMarketplaceLink, getOpenMarketplaceProductLink } from '../config/productConfig';
import { getIsOpenedInPopup } from '../popup/popup.redux';
import { PreviousButton } from './components/NavigationButtons';
import { getSelectedProduct, getSelectedProductId } from './redux/checkout.redux';
import type { Product } from './redux/types';

type GoogleAnalyticsEventAction = 'forwardToService' | 'goToCustomerCenter';

interface Props {
    subscriptionCreationInProgress: boolean;
    hasError: boolean;
    previousPath: string;
    isOpenedInPopup: boolean;
    productId?: string;
    product?: Product;
}

const CheckoutConfirmationPage = (props: Props) => {
    let content: ReactElement;
    const productId = props.productId;
    const isCompliant = props.product?.sku.toLowerCase() === 'man-sku00000004';
    const serviceLink = getApplicationLink(productId);
    const openMarketplaceProductLink = getOpenMarketplaceProductLink(productId);
    const openMarketplaceHomeLink = getOpenMarketplaceLink();
    const customerCenterContractsLink = `/${MARKETPLACE_CUSTOMER_CENTER_CONTRACTS}`;
    const getTrackingAttributes = (action: GoogleAnalyticsEventAction, trigger: 'click' | 'visibility') => ({
        'data-track-ga-event-trigger': trigger,
        'data-track-ga-event-category': 'marketplace',
        'data-track-ga-event-action': `${action}_${trigger}`,
        'data-track-ga-event-label': `productId::${productId}`,
    });
    const closePopup = () => {
        if (props.isOpenedInPopup) {
            setTimeout(() => {
                sendIFrameCloseMessageToParent();
                if (typeof window !== 'undefined') {
                    window.close();
                }
            }, 10);
        }
    };

    useEffect(() => {
        const success = !props.hasError && !props.subscriptionCreationInProgress;
        if (success && window.opener) {
            try {
                const message: BookingSuccessMessage = { type: SUCCESSFULLY_BOOKED, productId: props.productId };
                window.opener.postMessage(message, window.origin);
            } catch (e) {
                sendError(e);
            }
        }
    }, [props.hasError, props.subscriptionCreationInProgress, props.productId]);

    if (props.hasError) {
        content = (
            <ErrorState
                message={
                    <div className='display-flex flex-column gap-10'>
                        <FormattedMessage id={'marketplace.subscription.create.error'} />
                        <div className='display-flex justify-content-center margin-top-10'>
                            <PreviousButton previousPath={props.previousPath} />
                        </div>
                    </div>
                }
            />
        );
    } else if (props.subscriptionCreationInProgress) {
        content = (
            <Spinner text={<FormattedMessage id={'marketplace.subscription.create.loading'} />} isInverse={false} />
        );
    } else {
        content = (
            <div className='panel panel-default padding-20 padding-50-sm margin-x-auto max-width-700 margin-top-15pct'>
                <div className='text-center line-height-125rel margin-bottom-15 margin-top-10 margin-top-0-sm'>
                    <div className='text-size-h3 text-color-highlight text-medium margin-bottom-10 text-wrap-balance'>
                        <FormattedMessage id={'marketplace.checkout.success.title'} />
                    </div>
                    <div className='width-0 height-0 position-absolute left-50pct'>
                        <ConfettiExplosion duration={5000} colors={['#5296a3', '#69afbd', '#81c8d7', '#9ae1ef']} />
                    </div>
                    <div className='text-color-dark text-wrap-balance'>
                        <FormattedMessage id={'marketplace.checkout.success.subtitle'} />
                    </div>
                </div>
                {isCompliant && (
                    <div className='callout callout-info callout-condensed margin-bottom-20'>
                        <p className='margin-top-5 text-wrap-balance text-size-16'>
                            <FormattedMessage
                                id='marketplace.checkout.success.compliantcard'
                                values={{
                                    a: (chunks: ReactNode) => (
                                        <a
                                            href='https://remotedownload.rio.cloud/#guidance'
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            {chunks}
                                        </a>
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}
                <div className='display-flex flex-column flex-row-sm gap-20 padding-top-20'>
                    {serviceLink && (
                        <div className='panel panel-default panel-body bg-decent line-height-125rel flex-1-1-0 display-flex flex-column'>
                            <div className='text-size-20 text-bold margin-bottom-10'>
                                <FormattedMessage id={'marketplace.checkout.success.letsstart.title'} />
                            </div>
                            <div className='margin-bottom-20 flex-1-1'>
                                <FormattedMessage id={'marketplace.checkout.success.letsstart.text'} />
                            </div>
                            <Button
                                as='a'
                                href={serviceLink}
                                target='_blank'
                                rel={'noreferrer'}
                                onClick={() => closePopup()}
                                autoTrackingKey={'marketplace.checkout.success.letsstart.button'}
                                {...getTrackingAttributes('forwardToService', 'click')}
                                className={'ProductButton btn btn-primary btn-block'}
                            >
                                <FormattedMessage id={'marketplace.checkout.success.letsstart.button'} />
                            </Button>
                        </div>
                    )}

                    <div className='panel panel-default panel-body bg-decent line-height-125rel flex-1-1-0 display-flex flex-column'>
                        <div className='text-size-20 text-bold margin-bottom-10'>
                            <FormattedMessage id={'marketplace.checkout.success.managecontracts.title'} />
                        </div>
                        <div className='margin-bottom-20 flex-1-1'>
                            <FormattedMessage id={'marketplace.checkout.success.managecontracts.text'} />
                        </div>
                        <Button
                            as='a'
                            href={customerCenterContractsLink}
                            target='_blank'
                            rel={'noreferrer'}
                            autoTrackingKey={'marketplace.checkout.success.managecontracts.button'}
                            className={'ManageContractsButton btn btn-primary btn-outline btn-block'}
                        >
                            <FormattedMessage id={'marketplace.checkout.success.managecontracts.button'} />
                        </Button>
                    </div>
                </div>

                <div className='display-flex flex-column flex-row-sm gap-20 padding-top-20'>
                    {openMarketplaceProductLink && (
                        <div className='panel panel-default panel-body bg-decent line-height-125rel flex-1-1-0 display-flex flex-column'>
                            <div className='text-size-20 text-bold margin-bottom-10'>
                                <FormattedMessage id={'marketplace.checkout.success.howtouse.title'} />
                            </div>
                            <div className='margin-bottom-20 flex-1-1'>
                                <FormattedMessage id={'marketplace.checkout.success.howtouse.text'} />
                            </div>
                            <Button
                                as='a'
                                href={openMarketplaceProductLink}
                                target='_blank'
                                rel={'noreferrer'}
                                autoTrackingKey={'marketplace.checkout.success.howtouse.button'}
                                className={'OpenMarketplaceProductLink btn btn-primary btn-outline btn-block'}
                            >
                                <FormattedMessage id={'marketplace.checkout.success.howtouse.button'} />
                            </Button>
                        </div>
                    )}
                    <div className='panel panel-default panel-body bg-decent line-height-125rel flex-1-1-0 display-flex flex-column'>
                        <div className='text-size-20 text-bold margin-bottom-10'>
                            <FormattedMessage id={'marketplace.checkout.success.exploremore.title'} />
                        </div>
                        <div className='margin-bottom-20 flex-1-1'>
                            <FormattedMessage id={'marketplace.checkout.success.exploremore.text'} />
                        </div>
                        <Button
                            as='a'
                            href={openMarketplaceHomeLink}
                            target='_blank'
                            rel={'noreferrer'}
                            autoTrackingKey={'marketplace.checkout.success.exploremore.button'}
                            className={'OpenMarketplaceLink btn btn-primary btn-outline btn-block'}
                        >
                            <FormattedMessage id={'marketplace.checkout.success.exploremore.button'} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return <div className='Subscription'>{content}</div>;
};

const mapStateToProps = (state: RootState) => ({
    subscriptionCreationInProgress: getIsLoading(state, ApiCalls.SUBSCRIPTION_CREATE),
    hasError: getHasError(state, ApiCalls.SUBSCRIPTION_CREATE),
    isOpenedInPopup: getIsOpenedInPopup(state),
    productId: getSelectedProductId(state),
    product: getSelectedProduct(state),
});

export const CheckoutConfirmationPageContainer = connect(mapStateToProps)(CheckoutConfirmationPage);
